import { useCallback, useEffect, useMemo, useState } from "react";
import type { FiltroDef, Row, TabDef } from "../types";
import { api, ApiError } from "../api";
import { formatCell, lerLista, statusTone } from "../format";
import FiltrosBarra from "./FiltrosBarra";
import RecordForm from "./RecordForm";
import AnaliseOleoConfig from "./AnaliseOleoConfig";
import MaterialConferencia from "./MaterialConferencia";
import LimitesIA from "./LimitesIA";
import ErrorState from "./ErrorState";
import ImportarPlanilhaModal from "./ImportarPlanilhaModal";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorCampos } from "../ordem";
import { IconSearch, IconPlus, IconEdit, IconTrash, IconSend, IconCheck, IconFlask } from "../icons";

interface Props {
  tab: TabDef;
  onDataChanged: () => void; // avisa o App para recarregar dashboard
}

/**
 * Telas de DETALHE por recurso — o que abre nos dois cliques na linha.
 *
 * Antes isto era um `if (tab.resource === "analises_oleo")` repetido em cinco
 * pontos do render. Cada aba nova pedia mais um `if` em cada ponto, e esquecer
 * um deixava a linha clicável sem abrir nada. Aqui, tela nova = uma linha.
 *
 * `Componente` recebe sempre o mesmo contrato: o registro, `onCancel` e
 * `onSaved`.
 */
const MODAIS: Record<
  string,
  { Componente: any; titulo: string; dica: string }
> = {
  analises_oleo: {
    Componente: AnaliseOleoConfig,
    titulo: "Configurar / despachar o laudo",
    dica: "Dois cliques para abrir a configuração do laudo",
  },
  material_cliente: {
    Componente: MaterialConferencia,
    titulo: "Conferir a remessa (bipagem) e imprimir os documentos",
    dica: "Dois cliques para conferir a remessa com o leitor",
  },
};

/**
 * A linha passa neste filtro?
 *
 * O caso que exige cuidado é o "contem": o tipo de análise é uma LISTA (uma OS
 * pode pedir FQ e CR na mesma amostra). E lista VAZIA quer dizer "todas as
 * seções" — é assim que ficaram as análises antigas (ver db/schema.sql). Essas
 * precisam aparecer em qualquer filtro; escondê-las faria a pessoa achar que o
 * sistema perdeu registros.
 */
function casaFiltro(row: Row, f: FiltroDef, valor: string): boolean {
  if (!valor) return true;
  if (f.modo === "contem") {
    const lista = lerLista(row[f.campo]);
    if (lista.length === 0) return f.vazioContaComoTodos !== false;
    return lista.includes(valor);
  }
  return String(row[f.campo] ?? "") === valor;
}

/**
 * AÇÃO EXTRA na barra da listagem, por recurso — mesma ideia do MODAIS acima:
 * aba nova = uma linha aqui, sem `if` espalhado pelo render.
 */
const ACOES_BARRA: Record<string, { rotulo: string; Componente: any }> = {
  limites_normativos: { rotulo: "Trazer da norma (IA)", Componente: LimitesIA },
};

export default function CrudView({ tab, onDataChanged }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  /** Valor escolhido em cada filtro da barra (vazio = sem filtro). */
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [configurando, setConfigurando] = useState<Row | null>(null);
  /** Modal da ação extra da barra (ex.: trazer a norma com IA). */
  const [acaoAberta, setAcaoAberta] = useState(false);
  // Só nas abas que nasceram de uma planilha (ver TabDef.planilha).
  const [importando, setImportando] = useState(false);
  const [avisoPlanilha, setAvisoPlanilha] = useState<string | null>(null);

  // Excluir é exclusivo de administrador (regra do Portal): para os demais o
  // botão nem aparece — desabilitado só frustraria, já que o backend recusa.
  const podeExcluir = usePodeExcluir();

  // Qual tela de detalhe abre nos DOIS CLIQUES desta aba (e no botão de ação).
  // Ver MODAIS, no topo do arquivo.
  const modal = MODAIS[tab.resource];
  const acaoBarra = ACOES_BARRA[tab.resource];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    // Cada aba começa com os filtros que ela mesma define como padrão.
    const iniciais: Record<string, string> = {};
    for (const f of tab.filtros ?? []) if (f.padrao) iniciais[f.campo] = f.padrao;
    setFiltros(iniciais);
  }, [tab.key]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const defs = tab.filtros ?? [];
    const lista = rows.filter((r) => {
      for (const f of defs) if (!casaFiltro(r, f, filtros[f.campo] ?? "")) return false;
      if (!q) return true;
      return tab.fields.some((f) => String(r[f.name] ?? "").toLowerCase().includes(q));
    });
    // Cadastro por nome sai A→Z (ver src/ordem.ts e TabDef.ordenarPor); as
    // demais abas mantêm a ordem do servidor (a mais nova primeiro).
    return tab.ordenarPor?.length ? ordenarPorCampos(lista, tab.ordenarPor) : lista;
  }, [rows, search, filtros, tab.fields, tab.filtros, tab.ordenarPor]);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      await api.create(tab.resource, data);
    }
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  /** Ativa uma faixa sugerida pela IA (aba Limites normativos). */
  async function conferirFaixa(row: Row) {
    const nome = row.rotulo ?? row.parametro;
    if (!confirm(`Você conferiu "${nome}" contra o documento da norma?

Ao confirmar, esta faixa passa a valer nos diagnósticos.`)) return;
    try {
      await api.limites.conferir(row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao conferir a faixa");
    }
  }

  async function handleConfigSalva() {
    setConfigurando(null);
    await load();
    onDataChanged();
  }

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <FiltrosBarra
          filtros={tab.filtros ?? []}
          valores={filtros}
          onChange={(campo, valor) => setFiltros((v) => ({ ...v, [campo]: valor }))}
        />
        <div className="toolbar-right">
          {/*
            Com filtro ligado, mostrar só "3 registro(s)" faz parecer que o
            resto sumiu do sistema. O "de" deixa claro que é um recorte.
          */}
          <span className="badge">
            {filtered.length === rows.length
              ? `${rows.length} registro(s)`
              : `${filtered.length} de ${rows.length}`}
          </span>
          {acaoBarra && (
            <button className="btn-secondary" onClick={() => setAcaoAberta(true)}>
              <IconFlask size={17} /> {acaoBarra.rotulo}
            </button>
          )}
          {tab.planilha && (
            <>
              {/* Exportar vem antes de importar de propósito: quem vai mexer na
                  planilha por fora começa baixando o que já está no sistema. */}
              <button
                className="btn-secondary"
                title="Baixar a lista atual em Excel"
                onClick={() =>
                  api.planilha
                    .baixar(tab.planilha!, "exportar")
                    .catch((e) => setAvisoPlanilha(e?.message ?? "Não consegui gerar a planilha."))
                }
              >
                Exportar Excel
              </button>
              <button className="btn-secondary" onClick={() => setImportando(true)}>
                Importar Excel
              </button>
            </>
          )}
          <button className="btn-primary" onClick={openNew}>
            <IconPlus size={17} /> Novo
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className={modal ? "row-clickable" : undefined}
                  onDoubleClick={modal ? () => setConfigurando(row) : undefined}
                  title={modal?.dica}
                >
                  {tab.fields.filter((f) => !f.hideInTable).map((f) => {
                    const text = formatCell(f, row);
                    return (
                      <td key={f.name}>
                        {f.type === "status" && text !== "—" ? (
                          <span className={`pill pill-${statusTone(text)}`}>{text}</span>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                  <td
                    className="col-actions"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    {modal && (
                      <button
                        className="icon-btn"
                        title={modal.titulo}
                        onClick={() => setConfigurando(row)}
                      >
                        <IconSend size={17} />
                      </button>
                    )}
                    {/*
                      Faixa trazida da norma pela IA nasce DESLIGADA. Este botão
                      é o único caminho para ela valer no diagnóstico — e grava
                      quem conferiu, que é o que sustenta o laudo depois.
                    */}
                    {row.ativo === false && (
                      <button
                        className="icon-btn"
                        title="Conferi contra a norma — ativar esta faixa"
                        onClick={() => void conferirFaixa(row)}
                      >
                        <IconCheck size={17} />
                      </button>
                    )}
                    <button className="icon-btn" title="Editar" onClick={() => openEdit(row)}>
                      <IconEdit size={17} />
                    </button>
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir"
                        onClick={() => handleDelete(row)}
                      >
                        <IconTrash size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <RecordForm
          // O título fala do REGISTRO ("Nova quebra de frasco"), não da aba:
          // "Novo — Quebra de frasco/seringa" obrigava a traduzir o menu para
          // entender o que estava sendo criado.
          title={
            tab.singular
              ? `${editing ? "Editar" : tab.feminino ? "Nova" : "Novo"} ${tab.singular}`
              : `${editing ? "Editar" : "Novo"} — ${tab.label}`
          }
          subtitle={tab.formHint}
          icon={tab.icon}
          grupos={tab.grupos}
          fields={tab.fields}
          initial={editing}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {acaoAberta && acaoBarra && (
        <acaoBarra.Componente
          onCancel={() => setAcaoAberta(false)}
          onSaved={async () => {
            await load();
            onDataChanged();
          }}
        />
      )}

      {configurando && modal && (
        <modal.Componente
          analise={configurando}
          registro={configurando}
          onCancel={() => setConfigurando(null)}
          onSaved={handleConfigSalva}
        />
      )}

      {avisoPlanilha && (
        <div className="modal-notice">
          <span className="modal-notice-ic">✅</span>
          <span>{avisoPlanilha}</span>
          <button className="link-btn" onClick={() => setAvisoPlanilha(null)}>
            fechar
          </button>
        </div>
      )}

      {importando && tab.planilha && (
        <ImportarPlanilhaModal
          recurso={tab.planilha}
          titulo={tab.label}
          onClose={() => setImportando(false)}
          onImportado={(msg) => {
            setImportando(false);
            setAvisoPlanilha(msg);
            load();
          }}
        />
      )}
    </div>
  );
}
